import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import SuperAdminLayout from '@/components/admin/super-admin-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Settings, Users, Shield, Mail, Database, Bell, MailX, ToggleLeft, FileText, Workflow
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

// Type definitions for admin settings
interface PlatformSettings {
  platformName: string;
  platformDescription: string;
  supportEmail: string;
  maxTenantsAllowed: number;
  defaultTrial: boolean;
  trialDurationDays: number;
  maintenanceWindow: string;
}

interface UserManagement {
  allowSelfRegistration: boolean;
  requireEmailVerification: boolean;
  defaultUserRole: string;
  passwordComplexity: string;
  sessionDuration: number;
}

interface NotificationSettings {
  emailNotifications: boolean;
  renewalReminderEmails: boolean;
  monthlySpendReportEmails: boolean;
  systemAlerts: boolean;
  maintenanceNotices: boolean;
  billingAlerts: boolean;
  securityAlerts: boolean;
  stopAllEmails: boolean;
}

interface FeatureFlags {
  emailTemplatesEnabled: boolean;
  automationWorkflowsEnabled: boolean;
}

// Default values
const defaultPlatformSettings: PlatformSettings = {
  platformName: 'SAASGAMA',
  platformDescription: 'Enterprise software portfolio optimization platform',
  supportEmail: 'support@saasgama.com',
  maxTenantsAllowed: 1000,
  defaultTrial: true,
  trialDurationDays: 14,
  maintenanceWindow: '02:00-04:00'
};

const defaultUserManagement: UserManagement = {
  allowSelfRegistration: true,
  requireEmailVerification: true,
  defaultUserRole: 'user',
  passwordComplexity: 'medium',
  sessionDuration: 24
};

const defaultNotificationSettings: NotificationSettings = {
  emailNotifications: true,
  renewalReminderEmails: true,
  monthlySpendReportEmails: true,
  systemAlerts: true,
  maintenanceNotices: true,
  billingAlerts: true,
  securityAlerts: true,
  stopAllEmails: false,
};

const defaultFeatureFlags: FeatureFlags = {
  emailTemplatesEnabled: true,
  automationWorkflowsEnabled: true,
};

const AdminSettingsPage = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('platform');
  const { toast } = useToast();

  // Queries for settings data
  const { data: platformSettings = defaultPlatformSettings } = useQuery({
    queryKey: ['/api/admin/settings/platform'],
    queryFn: async () => {
      try {
        const response = await apiRequest<PlatformSettings>('/api/admin/settings/platform', { method: 'GET' });
        return response;
      } catch (error) {
        console.error('API error:', error);
        return defaultPlatformSettings;
      }
    },
  });

  const { data: userManagement = defaultUserManagement } = useQuery({
    queryKey: ['/api/admin/settings/users'],
    queryFn: async () => {
      try {
        const response = await apiRequest<UserManagement>('/api/admin/settings/users', { method: 'GET' });
        return response;
      } catch (error) {
        console.error('API error:', error);
        return defaultUserManagement;
      }
    },
  });

  const { data: notificationSettings = defaultNotificationSettings } = useQuery({
    queryKey: ['/api/admin/settings/notifications'],
    queryFn: async () => {
      try {
        const response = await apiRequest<NotificationSettings>('/api/admin/settings/notifications', { method: 'GET' });
        return response;
      } catch (error) {
        console.error('API error:', error);
        return defaultNotificationSettings;
      }
    },
  });

  const { data: featureFlags = defaultFeatureFlags } = useQuery({
    queryKey: ['/api/admin/settings/features'],
    queryFn: async () => {
      try {
        const response = await apiRequest<FeatureFlags>('/api/admin/settings/features', { method: 'GET' });
        return response;
      } catch (error) {
        console.error('API error:', error);
        return defaultFeatureFlags;
      }
    },
  });

  // Local state for optimistic/responsive updates
  const [emailTemplatesEnabledState, setEmailTemplatesEnabledState] = useState<boolean | null>(null);
  const [automationWorkflowsEnabledState, setAutomationWorkflowsEnabledState] = useState<boolean | null>(null);

  // Sync state when feature flags query data changes
  useEffect(() => {
    if (featureFlags) {
      setEmailTemplatesEnabledState(featureFlags.emailTemplatesEnabled);
      setAutomationWorkflowsEnabledState(featureFlags.automationWorkflowsEnabled);
    }
  }, [featureFlags]);

  // Mutations for updating settings
  const updatePlatformMutation = useMutation({
    mutationFn: async (data: PlatformSettings) => {
      return await apiRequest('/api/admin/settings/platform', {
        method: 'POST',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/settings/platform'] });
      toast({
        title: 'Platform settings updated',
        description: 'Platform configuration has been successfully updated.',
      });
    },
    onError: (error) => {
      console.error('Error updating platform settings:', error);
      toast({
        title: 'Update failed',
        description: 'Failed to update platform settings.',
        variant: 'destructive',
      });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async (data: UserManagement) => {
      return await apiRequest('/api/admin/settings/users', {
        method: 'POST',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/settings/users'] });
      toast({
        title: 'User settings updated',
        description: 'User management settings have been successfully updated.',
      });
    },
    onError: (error) => {
      console.error('Error updating user settings:', error);
      toast({
        title: 'Update failed',
        description: 'Failed to update user settings.',
        variant: 'destructive',
      });
    },
  });

  const updateNotificationMutation = useMutation({
    mutationFn: async (data: NotificationSettings) => {
      return await apiRequest('/api/admin/settings/notifications', {
        method: 'POST',
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/settings/notifications'] });
      toast({
        title: 'Notification settings updated',
        description: 'Notification preferences have been successfully updated.',
      });
    },
    onError: (error) => {
      console.error('Error updating notification settings:', error);
      toast({
        title: 'Update failed',
        description: 'Failed to update notification settings.',
        variant: 'destructive',
      });
    },
  });

  const updateFeatureFlagsMutation = useMutation({
    mutationFn: async (data: FeatureFlags) => {
      return await apiRequest('/api/admin/settings/features', {
        method: 'POST',
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/settings/features'] });
      toast({
        title: 'Feature settings updated',
        description: 'Feature flags have been updated successfully.',
      });
    },
    onError: () => {
      // Revert states on error
      if (featureFlags) {
        setEmailTemplatesEnabledState(featureFlags.emailTemplatesEnabled);
        setAutomationWorkflowsEnabledState(featureFlags.automationWorkflowsEnabled);
      }
      toast({
        title: 'Update failed',
        description: 'Failed to update feature flags.',
        variant: 'destructive',
      });
    },
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <SuperAdminLayout>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Admin Settings</h1>
            <p className="text-slate-500">Configure platform-wide settings and preferences</p>
          </div>
        </div>

        <Tabs defaultValue="platform" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-4 gap-1">
            <TabsTrigger value="platform" className="flex items-center gap-1">
              <Settings className="h-4 w-4" />
              <span className="hidden md:inline">Platform</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              <span className="hidden md:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-1">
              <Bell className="h-4 w-4" />
              <span className="hidden md:inline">Notifications</span>
            </TabsTrigger>
            <TabsTrigger value="features" className="flex items-center gap-1">
              <ToggleLeft className="h-4 w-4" />
              <span className="hidden md:inline">Features</span>
            </TabsTrigger>
          </TabsList>

          {/* Platform Settings Tab */}
          <TabsContent value="platform">
            <Card>
              <CardHeader>
                <CardTitle>Platform Configuration</CardTitle>
                <CardDescription>
                  Configure global platform settings and branding
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Platform Name</label>
                    <Input 
                      value={platformSettings.platformName}
                      onChange={(e) => {
                        const updatedSettings = { 
                          ...platformSettings, 
                          platformName: e.target.value 
                        };
                        updatePlatformMutation.mutate(updatedSettings);
                      }}
                      placeholder="Enter platform name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Support Email</label>
                    <Input 
                      type="email"
                      value={platformSettings.supportEmail}
                      onChange={(e) => {
                        const updatedSettings = { 
                          ...platformSettings, 
                          supportEmail: e.target.value 
                        };
                        updatePlatformMutation.mutate(updatedSettings);
                      }}
                      placeholder="support@example.com"
                    />
                  </div>
                  
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-sm font-medium">Platform Description</label>
                    <Input 
                      value={platformSettings.platformDescription}
                      onChange={(e) => {
                        const updatedSettings = { 
                          ...platformSettings, 
                          platformDescription: e.target.value 
                        };
                        updatePlatformMutation.mutate(updatedSettings);
                      }}
                      placeholder="Describe your platform"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Max Tenants Allowed</label>
                    <Input 
                      type="number"
                      value={platformSettings.maxTenantsAllowed?.toString() || '1000'}
                      onChange={(e) => {
                        const updatedSettings = { 
                          ...platformSettings, 
                          maxTenantsAllowed: parseInt(e.target.value) || 1000
                        };
                        updatePlatformMutation.mutate(updatedSettings);
                      }}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Trial Duration (Days)</label>
                    <Input 
                      type="number"
                      value={platformSettings.trialDurationDays?.toString() || '14'}
                      onChange={(e) => {
                        const updatedSettings = { 
                          ...platformSettings, 
                          trialDurationDays: parseInt(e.target.value) || 14
                        };
                        updatePlatformMutation.mutate(updatedSettings);
                      }}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Maintenance Window</label>
                    <Input 
                      value={platformSettings.maintenanceWindow || '02:00-04:00'}
                      onChange={(e) => {
                        const updatedSettings = { 
                          ...platformSettings, 
                          maintenanceWindow: e.target.value 
                        };
                        updatePlatformMutation.mutate(updatedSettings);
                      }}
                      placeholder="02:00-04:00"
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch 
                    checked={platformSettings.defaultTrial}
                    onCheckedChange={(checked) => {
                      const updatedSettings = { 
                        ...platformSettings, 
                        defaultTrial: checked 
                      };
                      updatePlatformMutation.mutate(updatedSettings);
                    }}
                  />
                  <label>Enable trial periods for new organizations</label>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Management Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>User Management</CardTitle>
                <CardDescription>
                  Configure user registration and access policies
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Switch 
                      checked={userManagement.allowSelfRegistration}
                      onCheckedChange={(checked) => {
                        const updatedSettings = { 
                          ...userManagement, 
                          allowSelfRegistration: checked 
                        };
                        updateUserMutation.mutate(updatedSettings);
                      }}
                    />
                    <label>Allow self-registration for new users</label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch 
                      checked={userManagement.requireEmailVerification}
                      onCheckedChange={(checked) => {
                        const updatedSettings = { 
                          ...userManagement, 
                          requireEmailVerification: checked 
                        };
                        updateUserMutation.mutate(updatedSettings);
                      }}
                    />
                    <label>Require email verification for new accounts</label>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Default User Role</label>
                      <Select 
                        value={userManagement.defaultUserRole}
                        onValueChange={(value) => {
                          const updatedSettings = { 
                            ...userManagement, 
                            defaultUserRole: value 
                          };
                          updateUserMutation.mutate(updatedSettings);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="viewer">Viewer</SelectItem>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Password Complexity</label>
                      <Select 
                        value={userManagement.passwordComplexity}
                        onValueChange={(value) => {
                          const updatedSettings = { 
                            ...userManagement, 
                            passwordComplexity: value 
                          };
                          updateUserMutation.mutate(updatedSettings);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Session Duration (Hours)</label>
                      <Input 
                        type="number"
                        value={userManagement.sessionDuration?.toString() || '24'}
                        onChange={(e) => {
                          const updatedSettings = { 
                            ...userManagement, 
                            sessionDuration: parseInt(e.target.value) || 24
                          };
                          updateUserMutation.mutate(updatedSettings);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Notification Settings</CardTitle>
                <CardDescription>
                  Configure system-wide notification preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  {/* Global email kill-switch */}
                  <div className={`flex items-center justify-between rounded-lg border p-4 ${notificationSettings.stopAllEmails ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                    <div className="flex items-center gap-3">
                      <MailX className={`h-5 w-5 ${notificationSettings.stopAllEmails ? 'text-red-600' : 'text-slate-400'}`} />
                      <div>
                        <p className="font-semibold text-sm">Stop All Emails</p>
                        <p className="text-xs text-slate-500">Block all outgoing emails except login &amp; password reset mails</p>
                      </div>
                    </div>
                    <Switch
                      checked={notificationSettings.stopAllEmails}
                      onCheckedChange={(checked) => {
                        const updatedSettings = {
                          ...notificationSettings,
                          stopAllEmails: checked,
                        };
                        updateNotificationMutation.mutate(updatedSettings);
                      }}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={notificationSettings.emailNotifications}
                      onCheckedChange={(checked) => {
                        const updatedSettings = {
                          ...notificationSettings,
                          emailNotifications: checked
                        };
                        updateNotificationMutation.mutate(updatedSettings);
                      }}
                    />
                    <label>Enable email notifications</label>
                  </div>

                  <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4">
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-slate-400" />
                      <div>
                        <p className="font-semibold text-sm">License renewal reminder emails</p>
                        <p className="text-xs text-slate-500">Send renewal reminders before license renewal dates</p>
                      </div>
                    </div>
                    <Switch
                      checked={notificationSettings.renewalReminderEmails}
                      disabled={notificationSettings.stopAllEmails || !notificationSettings.emailNotifications}
                      onCheckedChange={(checked) => {
                        const updatedSettings = {
                          ...notificationSettings,
                          renewalReminderEmails: checked
                        };
                        updateNotificationMutation.mutate(updatedSettings);
                      }}
                    />
                  </div>

                  <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4">
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-slate-400" />
                      <div>
                        <p className="font-semibold text-sm">Monthly SaaS spend reports</p>
                        <p className="text-xs text-slate-500">Send monthly spend summaries to tenant admins</p>
                      </div>
                    </div>
                    <Switch
                      checked={notificationSettings.monthlySpendReportEmails}
                      disabled={notificationSettings.stopAllEmails || !notificationSettings.emailNotifications}
                      onCheckedChange={(checked) => {
                        const updatedSettings = {
                          ...notificationSettings,
                          monthlySpendReportEmails: checked
                        };
                        updateNotificationMutation.mutate(updatedSettings);
                      }}
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch 
                      checked={notificationSettings.systemAlerts}
                      onCheckedChange={(checked) => {
                        const updatedSettings = { 
                          ...notificationSettings, 
                          systemAlerts: checked 
                        };
                        updateNotificationMutation.mutate(updatedSettings);
                      }}
                    />
                    <label>Show system alerts</label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch 
                      checked={notificationSettings.maintenanceNotices}
                      onCheckedChange={(checked) => {
                        const updatedSettings = { 
                          ...notificationSettings, 
                          maintenanceNotices: checked 
                        };
                        updateNotificationMutation.mutate(updatedSettings);
                      }}
                    />
                    <label>Send maintenance notices</label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch 
                      checked={notificationSettings.billingAlerts}
                      onCheckedChange={(checked) => {
                        const updatedSettings = { 
                          ...notificationSettings, 
                          billingAlerts: checked 
                        };
                        updateNotificationMutation.mutate(updatedSettings);
                      }}
                    />
                    <label>Enable billing alerts</label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch 
                      checked={notificationSettings.securityAlerts}
                      onCheckedChange={(checked) => {
                        const updatedSettings = { 
                          ...notificationSettings, 
                          securityAlerts: checked 
                        };
                        updateNotificationMutation.mutate(updatedSettings);
                      }}
                    />
                    <label>Enable security alerts</label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Feature Flags Tab */}
          <TabsContent value="features">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ToggleLeft className="h-5 w-5 text-blue-600" />
                  Feature Controls
                </CardTitle>
                <CardDescription>
                  Enable or disable platform features globally. Changes take effect immediately for all tenants.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">

                 {/* Email Templates */}
                <div className={`flex items-center justify-between rounded-lg border p-4 transition-colors ${
                  (emailTemplatesEnabledState ?? featureFlags.emailTemplatesEnabled)
                    ? 'border-blue-200 bg-blue-50'
                    : 'border-slate-200 bg-slate-50'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                      (emailTemplatesEnabledState ?? featureFlags.emailTemplatesEnabled) ? 'bg-blue-100' : 'bg-slate-100'
                    }`}>
                      <FileText className={`h-5 w-5 ${
                        (emailTemplatesEnabledState ?? featureFlags.emailTemplatesEnabled) ? 'text-blue-600' : 'text-slate-400'
                      }`} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">Email Templates</p>
                      <p className="text-xs text-slate-500">
                        Allow admins to create, edit and send custom email templates to tenants
                      </p>
                      <span className={`mt-1 inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
                        (emailTemplatesEnabledState ?? featureFlags.emailTemplatesEnabled)
                          ? 'bg-green-100 text-green-700'
                          : 'bg-slate-200 text-slate-500'
                      }`}>
                        {(emailTemplatesEnabledState ?? featureFlags.emailTemplatesEnabled) ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                  <Switch
                    id="toggle-email-templates"
                    checked={emailTemplatesEnabledState ?? featureFlags.emailTemplatesEnabled}
                    onCheckedChange={(checked) => {
                      setEmailTemplatesEnabledState(checked);
                      updateFeatureFlagsMutation.mutate({
                        emailTemplatesEnabled: checked,
                        automationWorkflowsEnabled: automationWorkflowsEnabledState ?? featureFlags.automationWorkflowsEnabled,
                      });
                    }}
                  />
                </div>

                {/* Automation Workflows */}
                <div className={`flex items-center justify-between rounded-lg border p-4 transition-colors ${
                  (automationWorkflowsEnabledState ?? featureFlags.automationWorkflowsEnabled)
                    ? 'border-purple-200 bg-purple-50'
                    : 'border-slate-200 bg-slate-50'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                      (automationWorkflowsEnabledState ?? featureFlags.automationWorkflowsEnabled) ? 'bg-purple-100' : 'bg-slate-100'
                    }`}>
                      <Workflow className={`h-5 w-5 ${
                        (automationWorkflowsEnabledState ?? featureFlags.automationWorkflowsEnabled) ? 'text-purple-600' : 'text-slate-400'
                      }`} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">Automation Workflows</p>
                      <p className="text-xs text-slate-500">
                        Enable automated rule-based workflows including triggers, actions, and scheduled jobs
                      </p>
                      <span className={`mt-1 inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
                        (automationWorkflowsEnabledState ?? featureFlags.automationWorkflowsEnabled)
                          ? 'bg-green-100 text-green-700'
                          : 'bg-slate-200 text-slate-500'
                      }`}>
                        {(automationWorkflowsEnabledState ?? featureFlags.automationWorkflowsEnabled) ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                  <Switch
                    id="toggle-automation-workflows"
                    checked={automationWorkflowsEnabledState ?? featureFlags.automationWorkflowsEnabled}
                    onCheckedChange={(checked) => {
                      setAutomationWorkflowsEnabledState(checked);
                      updateFeatureFlagsMutation.mutate({
                        emailTemplatesEnabled: emailTemplatesEnabledState ?? featureFlags.emailTemplatesEnabled,
                        automationWorkflowsEnabled: checked,
                      });
                    }}
                  />
                </div>

                <p className="text-xs text-slate-400 pt-2">
                  ⚠ Disabling a feature will hide it from the admin interface but does not delete any existing data.
                </p>

              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </SuperAdminLayout>
    </div>
  );
};

export default AdminSettingsPage;
